import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { DeactivatedProfilesStatsResponse, ExclusionKeyword } from '@shared/schemas';

import type { DeactivatedProfilesPage } from '../infrastructure/deactivated-profiles.gateway';
import { filtersChanged } from './deactivated-profiles.actions';
import { emptyPreview, emptyRestore, emptyScan, initialState } from './deactivated-profiles.state';

/**
 * Deactivated Profiles list slice. Reducers are pure state transitions only; the
 * paginated fetch and the bulk restore live in the saga, which reads `page` /
 * `pageSize` back from this state and dispatches these transitions as it progresses.
 * `pageSizeSet` resets to the first page (mirroring the old `useInfiniteApi`); the
 * saga triggers the refetch.
 */
const slice = createSlice({
  name: 'deactivatedProfiles',
  initialState,
  reducers: {
    // ── Cursor (the saga refetches after applying these) ──
    pageSet: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    pageSizeSet: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.page = 0;
    },

    // ── Load lifecycle ──
    loadStarted: (state) => {
      state.loading = true;
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<DeactivatedProfilesPage>) => {
      state.rows = action.payload.rows;
      state.total = action.payload.total;
      state.loading = false;
    },
    loadFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ── Header aggregates ──
    statsLoadSucceeded: (state, action: PayloadAction<DeactivatedProfilesStatsResponse>) => {
      state.stats = action.payload;
    },
    /**
     * Drop the figures rather than surface a message. Deliberately does NOT touch
     * `state.error`: that banner speaks for the list, and a table that loaded is
     * not broken because its header could not be counted.
     */
    statsLoadFailed: (state) => {
      state.stats = null;
    },

    // ── Bulk restore lifecycle ──
    restoreStarted: (state) => {
      state.restore = { status: 'restoring', count: 0, immunised: 0, error: null };
    },
    restoreSucceeded: (state, action: PayloadAction<{ count: number; immunised: number }>) => {
      state.restore = {
        status: 'succeeded',
        count: action.payload.count,
        immunised: action.payload.immunised,
        error: null,
      };
    },
    restoreFailed: (state, action: PayloadAction<string>) => {
      state.restore = { status: 'failed', count: 0, immunised: 0, error: action.payload };
    },
    /** Acknowledge a settled restore (page calls after the snackbar). */
    restoreCleared: (state) => {
      state.restore = emptyRestore;
    },

    // ── Manual scan lifecycle ──
    scanStarted: (state) => {
      state.scan = { status: 'scanning', count: 0, bypassed: 0, error: null };
    },
    scanSucceeded: (state, action: PayloadAction<{ count: number; bypassed: number }>) => {
      state.scan = {
        status: 'succeeded',
        count: action.payload.count,
        bypassed: action.payload.bypassed,
        error: null,
      };
    },
    scanFailed: (state, action: PayloadAction<string>) => {
      state.scan = { status: 'failed', count: 0, bypassed: 0, error: action.payload };
    },
    /** Acknowledge a settled scan (page calls after the snackbar). */
    scanCleared: (state) => {
      state.scan = emptyScan;
    },

    // ── Preview count (scan-confirm dialog) ──
    previewRequested: (state) => {
      state.preview = { status: 'loading', count: 0, bypassed: 0 };
    },
    previewSucceeded: (state, action: PayloadAction<{ count: number; bypassed: number }>) => {
      state.preview = {
        status: 'succeeded',
        count: action.payload.count,
        bypassed: action.payload.bypassed,
      };
    },
    previewFailed: (state) => {
      state.preview = { status: 'failed', count: 0, bypassed: 0 };
    },
    /** Reset the preview when the confirm dialog closes. */
    previewCleared: (state) => {
      state.preview = emptyPreview;
    },

    // ── Blocklist load ──
    keywordsLoadStarted: (state) => {
      state.keywords.loading = true;
      state.keywords.error = null;
    },
    keywordsLoadSucceeded: (state, action: PayloadAction<ExclusionKeyword[]>) => {
      state.keywords.loading = false;
      state.keywords.items = action.payload;
    },
    profileTypesLoaded: (state, action: PayloadAction<string[]>) => {
      state.profileTypes = action.payload;
    },
    keywordsLoadFailed: (state, action: PayloadAction<string>) => {
      state.keywords.loading = false;
      state.keywords.error = action.payload;
    },

    // ── Blocklist mutation (add / delete) ──
    keywordMutationStarted: (state) => {
      state.keywords.mutating = true;
      state.keywords.error = null;
    },
    keywordMutationFailed: (state, action: PayloadAction<string>) => {
      state.keywords.mutating = false;
      state.keywords.error = action.payload;
    },
    /** A mutation settled (the saga has refetched the list). */
    keywordMutationSettled: (state) => {
      state.keywords.mutating = false;
    },

    // ── Reactivated tab (#512): its own cursor and load lifecycle ──
    reactivatedPageSet: (state, action: PayloadAction<number>) => {
      state.reactivated.page = action.payload;
    },
    reactivatedPageSizeSet: (state, action: PayloadAction<number>) => {
      state.reactivated.pageSize = action.payload;
      state.reactivated.page = 0;
    },
    reactivatedLoadStarted: (state) => {
      state.reactivated.loading = true;
      state.reactivated.error = null;
    },
    reactivatedLoadSucceeded: (state, action: PayloadAction<DeactivatedProfilesPage>) => {
      state.reactivated.rows = action.payload.rows;
      state.reactivated.total = action.payload.total;
      state.reactivated.loading = false;
    },
    reactivatedLoadFailed: (state, action: PayloadAction<string>) => {
      state.reactivated.loading = false;
      state.reactivated.error = action.payload;
    },
  },
  /**
   * The one signal action the slice reacts to itself.
   *
   * Every other intent is applied by the saga, in the order its worker chooses.
   * This one cannot wait for that: the saga debounces the refetch by 300ms, the
   * search box is a controlled input, and an input that lags its own keystrokes
   * by a third of a second drops characters as fast as they are typed. So the
   * state moves at once and only the FETCH is debounced.
   *
   * Back to page 0 with it; page 4 of the unfiltered list is rarely page 4 of
   * the filtered one, and staying there shows an empty table over a filter that
   * matched plenty.
   */
  extraReducers: (builder) => {
    builder.addCase(filtersChanged, (state, action) => {
      state.filters = action.payload;
      state.page = 0;
    });
  },
});

export const deactivatedProfilesActions = slice.actions;
export const deactivatedProfilesReducer = slice.reducer;
